import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pencil, RotateCcw, Save, AlertTriangle, MailWarning, FileEdit, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

const CATEGORY_LABEL = {
  auth: 'Auth & Access',
  forum: 'Forum',
  message: 'Messages',
  britelab: 'BriteLabs',
  event: 'Events & Courses',
  community_invite: 'Invites',
  system: 'System & Admin',
};

/**
 * AdminEmailSettingsPane — exposes two stacked sections:
 *   1. Routing — master kill-switch per template
 *   2. Templates — edit subject + intro paragraph per template
 * Both are read from /api/admin/email-settings and saved against
 * /api/admin/email-settings/{routing|template}/{key}.
 */
export default function AdminEmailSettingsPane({ initialSection = 'routing' }) {
  const [section, setSection] = useState(initialSection);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ registry: {}, templates: {}, routing: {} });
  const [editing, setEditing] = useState(null); // template_key

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-settings`, { credentials: 'include' });
      if (!r.ok) throw new Error('load failed');
      setData(await r.json());
    } catch {
      toast.error('Failed to load email settings');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleRouting = async (key, next) => {
    setData(d => ({ ...d, routing: { ...d.routing, [key]: next } }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-settings/routing/${key}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!r.ok) throw new Error('failed');
      toast.success(`${data.registry[key]?.label || key}: ${next ? 'enabled' : 'disabled'}`);
    } catch {
      setData(d => ({ ...d, routing: { ...d.routing, [key]: !next } }));
      toast.error('Failed to update routing');
    }
  };

  const saveTemplate = async (key, body) => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-settings/template/${key}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'save failed');
      }
      toast.success('Template saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
    setSaving(false);
  };

  const resetTemplate = async (key) => {
    if (!window.confirm('Reset this template to platform defaults?')) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-settings/reset/${key}`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error('reset failed');
      toast.success('Template reset to defaults');
      load();
    } catch {
      toast.error('Failed to reset');
    }
  };

  // Group templates by category for display
  const grouped = Object.entries(data.registry || {}).reduce((acc, [key, meta]) => {
    const cat = meta.category || 'system';
    acc[cat] = acc[cat] || [];
    acc[cat].push([key, meta]);
    return acc;
  }, {});

  if (loading) return <div className="text-slate-400 text-center py-12" data-testid="email-settings-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-email-settings-pane">
      {/* Section switcher (sub-sub-tabs within Comms) */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
        <button onClick={() => setSection('routing')}
          data-testid="email-settings-tab-routing"
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            section === 'routing' ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}>
          <Send className="h-3.5 w-3.5 mr-1 inline" /> Routing
        </button>
        <button onClick={() => setSection('templates')}
          data-testid="email-settings-tab-templates"
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            section === 'templates' ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}>
          <FileEdit className="h-3.5 w-3.5 mr-1 inline" /> Templates
        </button>
      </div>

      {section === 'routing' && (
        <div data-testid="email-routing-section">
          <div className="mb-4">
            <h3 className="text-base font-bold text-slate-800">Email routing</h3>
            <p className="text-xs text-slate-500 mt-1">
              Master kill-switch per template. When off, the platform will skip the email entirely (per-user notification preferences still apply on top — this is the admin-level override).
            </p>
          </div>
          {Object.entries(grouped).map(([cat, items]) => (
            <Card key={cat} className="p-4 mb-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{CATEGORY_LABEL[cat] || cat}</p>
              <div className="divide-y divide-slate-100">
                {items.map(([key, meta]) => {
                  const enabled = data.routing[key] !== false;
                  return (
                    <div key={key} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{meta.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{meta.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!enabled && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">Disabled</Badge>}
                        <Switch
                          checked={enabled}
                          onCheckedChange={(v) => toggleRouting(key, v)}
                          data-testid={`route-toggle-${key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      {section === 'templates' && (
        <div data-testid="email-templates-section">
          <div className="mb-4">
            <h3 className="text-base font-bold text-slate-800">Email templates</h3>
            <p className="text-xs text-slate-500 mt-1">
              Override the subject line and intro paragraph per template. The rest of the email (header, CTA button, layout) stays consistent across the platform.
            </p>
          </div>
          {Object.entries(grouped).map(([cat, items]) => (
            <Card key={cat} className="p-4 mb-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{CATEGORY_LABEL[cat] || cat}</p>
              <div className="divide-y divide-slate-100">
                {items.map(([key, meta]) => {
                  const overrides = data.templates[key] || {};
                  const customised = overrides.subject || overrides.intro;
                  return (
                    <div key={key} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
                          {meta.label}
                          {customised && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Customised</Badge>}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">{meta.description}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {customised && (
                          <Button size="sm" variant="ghost"
                            onClick={() => resetTemplate(key)}
                            data-testid={`reset-tpl-${key}`}
                            title="Reset to default">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline"
                          onClick={() => setEditing(key)}
                          data-testid={`edit-tpl-${key}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      {editing && (
        <EditTemplateDialog
          template_key={editing}
          meta={data.registry[editing]}
          overrides={data.templates[editing] || {}}
          onClose={() => setEditing(null)}
          onSave={(body) => saveTemplate(editing, body)}
          saving={saving}
        />
      )}
    </div>
  );
}


function EditTemplateDialog({ template_key, meta, overrides, onClose, onSave, saving }) {
  const [subject, setSubject] = useState(overrides.subject || '');
  const [intro, setIntro] = useState(overrides.intro || meta.default_intro || '');
  const [sendingTest, setSendingTest] = useState(false);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await fetch(`/api/admin/email-settings/test/${template_key}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, intro }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to send test');
      toast.success(`Test sent to ${d.sent_to}`);
    } catch (e) {
      toast.error(e.message || 'Failed to send test');
    }
    setSendingTest(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="edit-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-brand-navy" /> {meta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500">{meta.description}</p>

          <div>
            <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">
              Subject line <span className="text-slate-400 font-normal">(leave blank to use the platform default)</span>
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="(default)"
              maxLength={250}
              data-testid="tpl-subject-input"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">
              Intro paragraph
            </Label>
            <Textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={5}
              maxLength={2000}
              data-testid="tpl-intro-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Replaces the first paragraph after "Hi {`{name}`},". The rest of the email body, links and buttons stay the same. Plain text only — HTML is escaped.
            </p>
            {meta.placeholders?.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-1.5">
                <span className="font-medium">Available placeholders:</span> {meta.placeholders.map(p => `{${p}}`).join(' · ')}
                <br />
                <span className="text-amber-600">Note (light mode):</span> placeholder substitution is not yet wired — keep dynamic refs out of the intro for now, mention specifics in subject line only.
              </p>
            )}
          </div>

          <div className="flex justify-between items-center pt-2 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="tpl-cancel-btn">Cancel</Button>
              <Button variant="outline" size="sm" onClick={sendTest} disabled={sendingTest}
                data-testid="tpl-send-test-btn">
                <Send className="h-3.5 w-3.5 mr-1" /> {sendingTest ? 'Sending…' : 'Send test to me'}
              </Button>
            </div>
            <Button size="sm" onClick={() => onSave({ subject, intro })}
              disabled={saving}
              className="bg-brand-navy text-white"
              data-testid="tpl-save-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
